import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import client from '../api/client';

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const TIMEZONES = [
  'Pacific/Honolulu','America/Los_Angeles','America/Denver','America/Chicago',
  'America/New_York','America/Halifax','America/Sao_Paulo','Atlantic/Azores',
  'Europe/London','Europe/Paris','Europe/Helsinki','Europe/Moscow',
  'Asia/Dubai','Asia/Karachi','Asia/Kolkata','Asia/Dhaka',
  'Asia/Bangkok','Asia/Singapore','Asia/Tokyo',
  'Australia/Sydney','Pacific/Auckland',
];

const tzCity = (tz) => tz.split('/').pop().replace(/_/g,' ');
const tzNow  = (tz) => new Date().toLocaleTimeString('en',{timeZone:tz,hour:'2-digit',minute:'2-digit',timeZoneName:'short'});

const hexToRgba = (hex, a) => {
  try {
    const h = hex.replace('#','');
    const r = parseInt(h.substring(0,2),16);
    const g = parseInt(h.substring(2,4),16);
    const b = parseInt(h.substring(4,6),16);
    return `rgba(${r},${g},${b},${a})`;
  } catch { return `rgba(0,153,255,${a})`; }
};

const fmtCalDate = (iso) => new Date(iso).toISOString().replace(/[-:]/g,'').split('.')[0]+'Z';

const tzToUtcMs = (dateStr, hh, mm, tz) => {
  const [y, mo, d] = dateStr.split('-').map(Number);
  const ref = new Date(Date.UTC(y, mo - 1, d, hh, mm, 0));
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz, year:'numeric', month:'2-digit', day:'2-digit',
    hour:'2-digit', minute:'2-digit', hour12:false,
  }).formatToParts(ref);
  const get = type => parseInt(parts.find(p=>p.type===type)?.value||'0');
  const shownMs = Date.UTC(get('year'), get('month')-1, get('day'), get('hour')%24, get('minute'), 0);
  return 2 * ref.getTime() - shownMs;
};

const fmtTimeInTz = (utcMs, tz) =>
  new Date(utcMs).toLocaleTimeString('en-US',{timeZone:tz,hour:'2-digit',minute:'2-digit',hour12:true});

const buildCalLinks = (title, startTime, endTime, location) => {
  const s = fmtCalDate(startTime);
  const e = fmtCalDate(endTime || new Date(new Date(startTime).getTime()+30*60000).toISOString());
  const enc = encodeURIComponent;
  return {
    google:  `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${enc(title)}&dates=${s}/${e}${location?`&location=${enc(location)}`:''}`,
    outlook: `https://outlook.live.com/calendar/0/deeplink/compose?subject=${enc(title)}&startdt=${enc(new Date(startTime).toISOString())}&enddt=${enc(new Date(endTime||startTime).toISOString())}${location?`&location=${enc(location)}`:''}`,
  };
};

const FormField = ({ field, value, onChange, primary }) => {
  const base = { width:'100%', padding:'10px 12px', borderRadius:8, border:'1px solid #d1d5db', fontSize:14, outline:'none', boxSizing:'border-box', marginTop:4 };
  switch (field.type) {
    case 'textarea':
      return <textarea style={{...base,resize:'vertical'}} rows={3} value={value||''} onChange={e=>onChange(e.target.value)} />;
    case 'select':
      return <select style={base} value={value||''} onChange={e=>onChange(e.target.value)}>
        <option value="">Select...</option>
        {(field.options||[]).map((o,i)=><option key={i}>{o}</option>)}
      </select>;
    case 'radio':
      return <div style={{marginTop:8,display:'flex',flexDirection:'column',gap:8}}>
        {(field.options||[]).map((o,i)=>(
          <label key={i} style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer'}}>
            <input type="radio" name={field.label} value={o} checked={value===o} onChange={()=>onChange(o)} style={{accentColor:primary,width:16,height:16}} />
            <span style={{fontSize:14,color:'#374151'}}>{o}</span>
          </label>
        ))}
      </div>;
    case 'checkbox':
      return <div style={{marginTop:8,display:'flex',flexDirection:'column',gap:8}}>
        {(field.options||[]).map((o,i)=>{
          const sel = Array.isArray(value)?value:[];
          return <label key={i} style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer'}}>
            <input type="checkbox" checked={sel.includes(o)} onChange={e=>onChange(e.target.checked?[...sel,o]:sel.filter(x=>x!==o))} style={{accentColor:primary,width:16,height:16}} />
            <span style={{fontSize:14,color:'#374151'}}>{o}</span>
          </label>;
        })}
      </div>;
    case 'multiselect':
      return <div style={{marginTop:8}}>
        <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
          {(field.options||[]).map((o,i)=>{
            const sel=Array.isArray(value)?value:[];
            const isSel=sel.includes(o);
            return <button key={i} type="button" onClick={()=>onChange(isSel?sel.filter(x=>x!==o):[...sel,o])}
              style={{padding:'6px 14px',borderRadius:20,border:`1px solid ${isSel?primary:'#d1d5db'}`,background:isSel?primary:'#fff',color:isSel?'#fff':'#374151',fontSize:13,cursor:'pointer'}}>
              {o}
            </button>;
          })}
        </div>
      </div>;
    default:
      return <input type={field.type||'text'} style={base} value={value||''} onChange={e=>onChange(e.target.value)} />;
  }
};

// ── Timezone Dropdown ────────────────────────────────────────
const TzDropdown = ({ value, onChange, primary }) => {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const ref                 = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = TIMEZONES.filter(tz =>
    tz.toLowerCase().includes(search.toLowerCase()) ||
    tzCity(tz).toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div ref={ref} style={{position:'relative'}}>
      <button
        onClick={() => { setOpen(!open); setSearch(''); }}
        style={{
          width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'8px 12px', borderRadius:8,
          border:`1px solid ${open ? primary : '#d1d5db'}`,
          background:'#fff', cursor:'pointer', fontSize:13, color:'#374151',
          outline:'none', transition:'border-color 0.15s',
        }}
      >
        <span style={{display:'flex',alignItems:'center',gap:7}}>
          <i className="ti ti-world" style={{fontSize:15,color:primary}} />
          <span style={{fontWeight:500}}>{tzCity(value)}</span>
        </span>
        <i className={`ti ti-chevron-${open?'up':'down'}`} style={{fontSize:13,color:'#9ca3af'}} />
      </button>

      {open && (
        <div style={{
          position:'absolute', top:'calc(100% + 6px)', left:0, right:0, zIndex:999,
          background:'#fff', borderRadius:10, border:'1px solid #e5e7eb',
          boxShadow:'0 8px 24px rgba(0,0,0,0.12)', overflow:'hidden',
        }}>
          <div style={{padding:'8px 10px',borderBottom:'1px solid #f3f4f6'}}>
            <input
              autoFocus
              placeholder="Search timezone..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{width:'100%',padding:'6px 10px',borderRadius:7,border:'1px solid #e5e7eb',fontSize:12,outline:'none',boxSizing:'border-box'}}
            />
          </div>
          <div style={{maxHeight:200,overflowY:'auto'}}>
            {filtered.map(tz => (
              <div
                key={tz}
                onClick={() => { onChange(tz); setOpen(false); }}
                style={{
                  display:'flex', alignItems:'center', justifyContent:'space-between',
                  padding:'9px 14px', cursor:'pointer', fontSize:12,
                  background: value===tz ? `${primary}12` : 'transparent',
                  borderLeft: value===tz ? `3px solid ${primary}` : '3px solid transparent',
                  transition:'background 0.1s',
                }}
                onMouseEnter={e=>{ if(value!==tz) e.currentTarget.style.background='#f9fafb'; }}
                onMouseLeave={e=>{ if(value!==tz) e.currentTarget.style.background='transparent'; }}
              >
                <span style={{fontWeight: value===tz?600:400, color: value===tz?primary:'#374151'}}>
                  {tzCity(tz)}
                  <span style={{fontWeight:400,color:'#9ca3af',fontSize:11,marginLeft:6}}>{tz.split('/')[0]}</span>
                </span>
                <span style={{color:'#6b7280',fontSize:11,whiteSpace:'nowrap',marginLeft:8}}>{tzNow(tz)}</span>
              </div>
            ))}
            {filtered.length===0 && (
              <div style={{padding:'16px',textAlign:'center',fontSize:12,color:'#9ca3af'}}>No timezone found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function BookingPage() {
  const { slug }       = useParams();
  const [page,         setPage]         = useState(null);
  const [brand,        setBrand]        = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [step,         setStep]         = useState(1);
  const [selDate,      setSelDate]      = useState('');
  const [selTime,      setSelTime]      = useState('');
  const [bookedSlots,  setBookedSlots]  = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [timezone,     setTimezone]     = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata'
  );
  // ── CHANGED: split name into first_name + last_name ──
  const [form,         setForm]         = useState({ first_name:'', last_name:'', email:'', phone:'', notes:'' });
  const [fieldVals,    setFieldVals]    = useState({});
  const [answers,      setAnswers]      = useState({});
  const [submitting,   setSubmitting]   = useState(false);
  const [bookingResult,setBookingResult]= useState(null);
  const [calYear,      setCalYear]      = useState(new Date().getFullYear());
  const [calMonth,     setCalMonth]     = useState(new Date().getMonth());

  // ── IP-based timezone auto-detection ──────────────────────
  useEffect(() => {
    fetch('https://ipapi.co/timezone/')
      .then(r => r.text())
      .then(tz => {
        const clean = tz.trim();
        if (clean && clean.includes('/') && !clean.startsWith('<')) {
          setTimezone(clean);
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    Promise.all([
      client.get(`/api/calendar/public/${slug}`),
      client.get('/api/settings/brand').catch(()=>({data:null})),
    ])
      .then(([p,b]) => { setPage(p.data); setBrand(b.data); })
      .catch(() => setError('Booking page not found or no longer active.'))
      .finally(() => setLoading(false));
  }, [slug]); // eslint-disable-line

  useEffect(() => {
    if (!selDate || !page) return;
    setSlotsLoading(true);
    client.get(`/api/calendar/booked-slots/${page.id}?date=${selDate}`)
      .then(({ data }) => setBookedSlots(data||[]))
      .catch(() => setBookedSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [selDate, page]); // eslint-disable-line

  const today       = new Date();
  const daysInMonth = new Date(calYear, calMonth+1, 0).getDate();
  const firstDay    = new Date(calYear, calMonth, 1).getDay();
  const hostTz      = page?.host_timezone || 'Asia/Kolkata';

  const isAvailable = (dateStr) => {
    const d = new Date(dateStr+'T12:00:00');
    if (d < new Date(today.toDateString())) return false;
    const dayName = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][d.getDay()];
    return page?.availability?.[dayName]?.enabled;
  };

  const isBooked = (hostTimeStr) => {
    if (!selDate || !bookedSlots.length) return false;
    const [hh, mm] = hostTimeStr.split(':').map(Number);
    const slotUtcMs = tzToUtcMs(selDate, hh, mm, hostTz);
    return bookedSlots.some(iso => Math.abs(new Date(iso).getTime() - slotUtcMs) < 60000);
  };

  const ALL_TIMES = [];
  if (page && selDate) {
    const dayName = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][new Date(selDate+'T12:00:00').getDay()];
    const avail = page.availability?.[dayName];
    if (avail?.enabled) {
      const [sh,sm] = (avail.start||'09:00').split(':').map(Number);
      const [eh,em] = (avail.end  ||'17:00').split(':').map(Number);
      let cur = sh*60+sm;
      const end = eh*60+em;
      while (cur + (page.duration_mins||30) <= end) {
        ALL_TIMES.push(`${String(Math.floor(cur/60)).padStart(2,'0')}:${String(cur%60).padStart(2,'0')}`);
        cur += page.duration_mins||30;
      }
    }
  }

  // ── CHANGED: build full name from first + last, send both to API ──
  const handleSubmit = async () => {
    if (!form.first_name || !form.email) return;
    setSubmitting(true);
    setError('');
    try {
      const [hh, mm] = selTime.split(':').map(Number);
      const startMs  = tzToUtcMs(selDate, hh, mm, hostTz);
      const start    = new Date(startMs);
      const end      = new Date(startMs + (page.duration_mins||30)*60000);
      const fullName = [form.first_name, form.last_name].filter(Boolean).join(' ');
      const { data } = await client.post('/api/calendar/bookings', {
        booking_page_id: page.id,
        booker_name:     fullName,
        booker_first_name: form.first_name,
        booker_last_name:  form.last_name,
        booker_email:    form.email,
        booker_phone:    form.phone,
        start_time:      start.toISOString(),
        end_time:        end.toISOString(),
        notes:           form.notes,
        answers:         {...answers,...fieldVals},
        booker_timezone: timezone,
      });
      setBookingResult({ ...data, startTime:start.toISOString(), endTime:end.toISOString() });
      setStep(4);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to book. Please try again.');
    } finally { setSubmitting(false); }
  };

  const handleTzChange = (tz) => {
    setTimezone(tz);
    setSelDate('');
    setSelTime('');
    setStep(1);
  };

  const primary      = page?.color || '#0099FF';
  const primaryLight = hexToRgba(primary, 0.1);
  const appName      = brand?.app_name || 'Digital Xtension';
  const currentYear  = new Date().getFullYear();
  const inp = { width:'100%', padding:'10px 12px', borderRadius:8, border:'1px solid #d1d5db', fontSize:14, outline:'none', boxSizing:'border-box', marginTop:4 };

  const slotDisplay = (hostTimeStr) => {
    const [hh, mm] = hostTimeStr.split(':').map(Number);
    return fmtTimeInTz(tzToUtcMs(selDate, hh, mm, hostTz), timezone);
  };

  if (loading) return (
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#f5f7fa'}}>
      <div style={{width:36,height:36,border:'3px solid #e5e7eb',borderTop:`3px solid #0099FF`,borderRadius:'50%',animation:'spin 0.8s linear infinite'}} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (error&&!page) return (
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#f5f7fa',fontFamily:"'Inter',system-ui,sans-serif"}}>
      <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/tabler-icons.min.css" />
      <div style={{textAlign:'center',padding:40}}>
        <i className="ti ti-calendar-off" style={{fontSize:48,color:'#d1d5db',display:'block',marginBottom:16}} />
        <div style={{fontSize:18,fontWeight:600,color:'#374151',marginBottom:8}}>Page Not Found</div>
        <div style={{fontSize:14,color:'#6b7280'}}>{error}</div>
      </div>
    </div>
  );

  return (
    <div style={{minHeight:'100vh',background:'#f5f7fa',fontFamily:"'Inter',system-ui,sans-serif",display:'flex',flexDirection:'column'}}>
      <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/tabler-icons.min.css" />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* ── Top bar ── */}
      <div style={{background:primary,padding:'14px 20px',textAlign:'center'}}>
        {page.logo_url
          ? <img src={page.logo_url} alt={page.title} style={{maxHeight:60,maxWidth:300,width:'auto',height:'60px',objectFit:'contain',display:  'block',margin:'0 auto',filter:'brightness(0) invert(1)'}} />
          : <div style={{fontSize:16,fontWeight:700,color:'#fff',letterSpacing:0.5}}>{page.title}</div>
        }
      </div>

      {/* ── Main widget ── */}
      <div style={{flex:1,padding:'32px 20px'}}>
        <div style={{maxWidth:940,margin:'0 auto',background:'#fff',borderRadius:16,overflow:'visible',boxShadow:'0 4px 24px rgba(0,0,0,0.08)',display:'flex',minHeight:520}}>

          {/* ── Left panel ── */}
          <div style={{width:290,background:'#f9fafb',borderRight:'1px solid #e5e7eb',padding:28,flexShrink:0,display:'flex',flexDirection:'column',borderRadius:'16px 0 0 16px',overflow:'visible'}}>
            {page.host_photo_url && (
              <div style={{textAlign:'center',marginBottom:20}}>
                <img src={page.host_photo_url} alt="Host"
                  style={{width:72,height:72,borderRadius:'50%',objectFit:'cover',border:`3px solid ${primary}`,display:'block',margin:'0 auto'}} />
              </div>
            )}
            <div style={{width:40,height:4,background:primary,borderRadius:2,marginBottom:16}} />
            <div style={{fontWeight:700,fontSize:19,color:'#151515',marginBottom:8}}>{page.title}</div>
            {page.description && <div style={{fontSize:13,color:'#6b7280',marginBottom:16,lineHeight:1.6}}>{page.description}</div>}
            <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:20}}>
              <div style={{display:'flex',alignItems:'center',gap:10,fontSize:13,color:'#374151'}}>
                <i className="ti ti-clock" style={{fontSize:17,color:primary}} />{page.duration_mins} minutes
              </div>
              {page.location && (
                <div style={{display:'flex',alignItems:'center',gap:10,fontSize:13,color:'#374151'}}>
                  <i className="ti ti-map-pin" style={{fontSize:17,color:primary}} />{page.location}
                </div>
              )}
            </div>

            {/* ── Timezone picker ── */}
            <div style={{marginBottom:16}}>
              <div style={{fontSize:11,fontWeight:600,color:'#9ca3af',letterSpacing:0.5,marginBottom:6}}>TIMEZONE</div>
              <TzDropdown value={timezone} onChange={handleTzChange} primary={primary} />
            </div>

            {/* ── Selected summary ── */}
            {step>1 && selDate && (
              <div style={{marginTop:'auto',paddingTop:8}}>
                <div style={{padding:'12px 14px',background:'#fff',borderRadius:10,border:`1px solid ${hexToRgba(primary,0.3)}`}}>
                  <div style={{fontSize:11,color:'#9ca3af',marginBottom:4}}>SELECTED</div>
                  <div style={{fontWeight:600,fontSize:14,color:'#151515'}}>
                    {new Date(selDate+'T12:00:00').toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long'})}
                  </div>
                  {selTime && (
                    <div style={{fontSize:13,color:primary,marginTop:2,fontWeight:500}}>
                      {slotDisplay(selTime)}
                      <span style={{fontSize:10,color:'#9ca3af',marginLeft:5}}>({tzCity(timezone)})</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── Right panel ── */}
          <div style={{flex:1,padding:32,overflowY:'auto'}}>

            {/* Step 1 — Pick date */}
            {step===1 && (
              <div>
                <div style={{fontWeight:600,fontSize:16,color:'#151515',marginBottom:4}}>Select a Date</div>
                <div style={{fontSize:12,color:'#9ca3af',marginBottom:20}}>
                  Times shown in <strong>{tzCity(timezone)}</strong>
                </div>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
                  <button onClick={()=>{if(calMonth===0){setCalMonth(11);setCalYear(calYear-1);}else setCalMonth(calMonth-1);}}
                    style={{background:'none',border:'1px solid #e5e7eb',borderRadius:8,padding:'6px 14px',cursor:'pointer',fontSize:16,color:'#374151'}}>‹</button>
                  <div style={{fontWeight:600,fontSize:15}}>{MONTH_NAMES[calMonth]} {calYear}</div>
                  <button onClick={()=>{if(calMonth===11){setCalMonth(0);setCalYear(calYear+1);}else setCalMonth(calMonth+1);}}
                    style={{background:'none',border:'1px solid #e5e7eb',borderRadius:8,padding:'6px 14px',cursor:'pointer',fontSize:16,color:'#374151'}}>›</button>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:4,marginBottom:8}}>
                  {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d=>(
                    <div key={d} style={{textAlign:'center',fontSize:12,fontWeight:600,color:'#9ca3af',padding:'6px 0'}}>{d}</div>
                  ))}
                </div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:4}}>
                  {Array(firstDay).fill(null).map((_,i)=><div key={`e${i}`} />)}
                  {Array(daysInMonth).fill(null).map((_,i)=>{
                    const day     = i+1;
                    const dateStr = `${calYear}-${String(calMonth+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                    const avail   = isAvailable(dateStr);
                    const isSel   = selDate===dateStr;
                    const isPast  = new Date(dateStr+'T12:00:00') < new Date(today.toDateString());
                    return (
                      <button key={day} onClick={()=>avail&&(setSelDate(dateStr),setStep(2))} disabled={!avail}
                        style={{
                          padding:'10px 0', borderRadius:8,
                          border: isSel?`2px solid ${primary}`:avail?`1px solid ${hexToRgba(primary,0.2)}`:'none',
                          background: isSel?primary:avail?primaryLight:'transparent',
                          color: isSel?'#fff':avail?primary:isPast?'#b0b0b0':'#c0c0c0',
                          fontWeight: isSel?700:avail?500:400,
                          fontSize:14, cursor:avail?'pointer':'not-allowed', transition:'all 0.1s',
                        }}>
                        {day}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 2 — Pick time */}
            {step===2 && (
              <div>
                <button onClick={()=>setStep(1)} style={{background:'none',border:'none',cursor:'pointer',color:'#6b7280',fontSize:13,marginBottom:16,display:'flex',alignItems:'center',gap:6}}>
                  <i className="ti ti-arrow-left" /> Back
                </button>
                <div style={{fontWeight:600,fontSize:16,color:'#151515',marginBottom:4}}>
                  {new Date(selDate+'T12:00:00').toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long'})}
                </div>
                <div style={{fontSize:12,color:'#9ca3af',marginBottom:20}}>Times in <strong>{tzCity(timezone)}</strong></div>
                {slotsLoading ? (
                  <div style={{textAlign:'center',padding:20,color:'#9ca3af',fontSize:13}}>Loading available times...</div>
                ) : ALL_TIMES.length===0 ? (
                  <div style={{textAlign:'center',padding:20,color:'#9ca3af',fontSize:13}}>No available slots on this day.</div>
                ) : (
                  <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10}}>
                    {ALL_TIMES.map(t=>{
                      const taken   = isBooked(t);
                      const isSel   = selTime===t;
                      const display = selDate ? slotDisplay(t) : t;
                      return (
                        <button key={t} onClick={()=>!taken&&(setSelTime(t),setStep(3))} disabled={taken}
                          style={{
                            padding:'10px 14px', borderRadius:8,
                            border: taken?'1px solid #e5e7eb':isSel?`2px solid ${primary}`:`1px solid ${hexToRgba(primary,0.25)}`,
                            background: taken?'#f9fafb':isSel?primary:primaryLight,
                            color: taken?'#c0c0c0':isSel?'#fff':primary,
                            fontSize:14, fontWeight:isSel?700:500,
                            cursor:taken?'not-allowed':'pointer', transition:'all 0.1s',
                          }}>
                          {display}
                          {taken && <div style={{fontSize:10,color:'#d1d5db',marginTop:2}}>Booked</div>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Step 3 — Details */}
            {step===3 && (
              <div>
                <button onClick={()=>setStep(2)} style={{background:'none',border:'none',cursor:'pointer',color:'#6b7280',fontSize:13,marginBottom:16,display:'flex',alignItems:'center',gap:6}}>
                  <i className="ti ti-arrow-left" /> Back
                </button>
                <div style={{fontWeight:600,fontSize:16,color:'#151515',marginBottom:20}}>Your Details</div>
                <div style={{display:'grid',gap:14,marginBottom:16}}>

                  {/* ── CHANGED: First Name + Last Name side by side ── */}
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                    <div>
                      <label style={{fontSize:13,fontWeight:500,color:'#374151'}}>First Name *</label>
                      <input style={inp} placeholder="First name" value={form.first_name} onChange={e=>setForm({...form,first_name:e.target.value})} />
                    </div>
                    <div>
                      <label style={{fontSize:13,fontWeight:500,color:'#374151'}}>Last Name</label>
                      <input style={inp} placeholder="Last name" value={form.last_name} onChange={e=>setForm({...form,last_name:e.target.value})} />
                    </div>
                  </div>

                  <div><label style={{fontSize:13,fontWeight:500,color:'#374151'}}>Email *</label><input style={inp} type="email" placeholder="your@email.com" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} /></div>
                  <div><label style={{fontSize:13,fontWeight:500,color:'#374151'}}>Phone</label><input style={inp} placeholder="+91 98765 43210" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} /></div>
                  {(page.form_fields||[]).map((field,i)=>(
                    <div key={i}>
                      <label style={{fontSize:13,fontWeight:500,color:'#374151'}}>{field.label}{field.required&&<span style={{color:'#EF4444'}}> *</span>}</label>
                      <FormField field={field} value={fieldVals[field.label]} onChange={val=>setFieldVals({...fieldVals,[field.label]:val})} primary={primary} />
                    </div>
                  ))}
                  {(page.questions||[]).map((q,i)=>(
                    <div key={i}><label style={{fontSize:13,fontWeight:500,color:'#374151'}}>{q.label}{q.required&&<span style={{color:'#EF4444'}}> *</span>}</label><input style={inp} value={answers[q.label]||''} onChange={e=>setAnswers({...answers,[q.label]:e.target.value})} /></div>
                  ))}
                  <div><label style={{fontSize:13,fontWeight:500,color:'#374151'}}>Additional Notes</label><textarea style={{...inp,resize:'vertical'}} rows={3} value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} /></div>
                </div>
                {error && <div style={{color:'#EF4444',fontSize:13,marginBottom:12,background:'#fef2f2',borderRadius:8,padding:'8px 12px'}}>{error}</div>}
                <button onClick={handleSubmit} disabled={submitting||!form.first_name||!form.email}
                  style={{width:'100%',padding:12,borderRadius:10,border:'none',background:primary,color:'#fff',fontSize:15,fontWeight:600,cursor:submitting?'wait':'pointer',opacity:submitting?0.7:1,transition:'opacity 0.15s'}}>
                  {submitting
                    ? <span style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8}}><span style={{width:16,height:16,border:'2px solid rgba(255,255,255,0.4)',borderTop:'2px solid #fff',borderRadius:'50%',display:'inline-block',animation:'spin 0.8s linear infinite'}} />Booking...</span>
                    : 'Confirm Booking'}
                </button>
              </div>
            )}

            {/* Step 4 — Confirmed */}
            {step===4 && bookingResult && (()=>{
              const startIso    = bookingResult.startTime;
              const endIso      = bookingResult.endTime;
              const startMs     = new Date(startIso).getTime();
              const calLinks    = buildCalLinks(page.title, startIso, endIso, page.location);
              const displayTime = fmtTimeInTz(startMs, timezone);
              const displayDate = new Date(startMs).toLocaleDateString('en-IN',{timeZone:timezone,weekday:'long',day:'numeric',month:'long',year:'numeric'});
              const ical = [
                'BEGIN:VCALENDAR','VERSION:2.0','PRODID:-//Digital Xtension//EN',
                'BEGIN:VEVENT',
                `DTSTART:${fmtCalDate(startIso)}`,
                `DTEND:${fmtCalDate(endIso)}`,
                `SUMMARY:${page.title}`,
                page.location?`LOCATION:${page.location}`:'',
                'STATUS:CONFIRMED',
                'END:VEVENT','END:VCALENDAR',
              ].filter(Boolean).join('\r\n');
              const icalHref = `data:text/calendar;charset=utf-8,${encodeURIComponent(ical)}`;
              return (
                <div style={{textAlign:'center',paddingTop:32}}>
                  <div style={{width:64,height:64,borderRadius:'50%',background:primaryLight,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 16px'}}>
                    <i className="ti ti-circle-check" style={{fontSize:36,color:primary}} />
                  </div>
                  <div style={{fontWeight:700,fontSize:22,color:'#151515',marginBottom:8}}>Booking Confirmed!</div>
                  <div style={{fontSize:14,color:'#6b7280',marginBottom:20}}>
                    A confirmation has been sent to <strong>{form.email}</strong>
                  </div>
                  <div style={{background:primaryLight,border:`1px solid ${hexToRgba(primary,0.3)}`,borderRadius:12,padding:20,maxWidth:360,margin:'0 auto 24px',textAlign:'left'}}>
                    <div style={{marginBottom:10}}>
                      <div style={{fontSize:11,color:'#9ca3af'}}>MEETING</div>
                      <div style={{fontWeight:600,fontSize:15}}>{page.title}</div>
                    </div>
                    <div style={{marginBottom:10}}>
                      <div style={{fontSize:11,color:'#9ca3af'}}>DATE & TIME</div>
                      <div style={{fontWeight:500,fontSize:14}}>{displayDate}</div>
                      <div style={{fontWeight:600,fontSize:15,color:primary,marginTop:2}}>{displayTime}</div>
                      <div style={{fontSize:11,color:'#9ca3af',marginTop:2}}>{tzCity(timezone)} — {timezone}</div>
                    </div>
                    <div>
                      <div style={{fontSize:11,color:'#9ca3af'}}>DURATION</div>
                      <div style={{fontWeight:500,fontSize:14}}>{page.duration_mins} minutes</div>
                    </div>
                  </div>
                  <div style={{marginBottom:24}}>
                    <div style={{fontSize:13,fontWeight:600,color:'#374151',marginBottom:12}}>Add to your calendar:</div>
                    <div style={{display:'flex',gap:10,justifyContent:'center',flexWrap:'wrap'}}>
                      <a href={calLinks.google} target="_blank" rel="noreferrer"
                        style={{display:'inline-flex',alignItems:'center',gap:8,padding:'10px 16px',borderRadius:10,border:`1px solid ${hexToRgba(primary,0.3)}`,background:primaryLight,textDecoration:'none',fontSize:13,color:primary,fontWeight:500}}>
                        <i className="ti ti-brand-google" style={{fontSize:16}} /> Google Calendar
                      </a>
                      <a href={calLinks.outlook} target="_blank" rel="noreferrer"
                        style={{display:'inline-flex',alignItems:'center',gap:8,padding:'10px 16px',borderRadius:10,border:`1px solid ${hexToRgba(primary,0.3)}`,background:primaryLight,textDecoration:'none',fontSize:13,color:primary,fontWeight:500}}>
                        <i className="ti ti-brand-windows" style={{fontSize:16}} /> Outlook
                      </a>
                      <a href={icalHref} download={`${page.slug}-booking.ics`}
                        style={{display:'inline-flex',alignItems:'center',gap:8,padding:'10px 16px',borderRadius:10,border:`1px solid ${hexToRgba(primary,0.3)}`,background:primaryLight,textDecoration:'none',fontSize:13,color:primary,fontWeight:500}}>
                        <i className="ti ti-calendar-download" style={{fontSize:16}} /> iCalendar (.ics)
                      </a>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      <div style={{textAlign:'center',padding:'14px 20px',borderTop:'1px solid #e5e7eb',background:'#fff'}}>
        <div style={{fontSize:12,color:'#9ca3af'}}>© {currentYear} {appName}. All rights reserved.</div>
      </div>
    </div>
  );
}
